import { Form, Input, Radio, Select, Space, Typography } from "antd";
import { useEffect } from "react";
import { FluxoResposta } from "../cadastroCotaUsuario.types";
import {
  MOTIVO_OPTIONS,
  OnboardingFrame,
  SubmitButton,
} from "../utils";

interface Props {
  fluxo: FluxoResposta;
  submitting: boolean;
  onConcluir: (payload: Record<string, unknown>) => Promise<void>;
}

const scopeLabels: Record<string, { titulo: string; descricao: string }> = {
  TODOS_OS_PERIODOS: {
    titulo: "Todos os meus períodos",
    descricao: "Quero receber oportunidades para todos os meus períodos.",
  },
  APENAS_ALGUNS_PERIODOS: {
    titulo: "Apenas alguns períodos",
    descricao: "Quero escolher para quais períodos desejo receber oportunidades.",
  },
  NAO_DESEJO_RECEBER_OPORTUNIDADES: {
    titulo: "Nenhum neste momento",
    descricao: "Não quero disponibilizar períodos agora.",
  },
};

export function EtapaDisponibilidade({
  fluxo,
  submitting,
  onConcluir,
}: Props) {
  const [form] = Form.useForm();
  const escopo = Form.useWatch("escopoDisponibilizacao", form);
  const motivo = Form.useWatch("motivo", form);
  const dadosDisponibilidade =
    (fluxo.dadosPreenchidos?.disponibilidade as Record<string, unknown> | undefined) ??
    {};
  const escopoDisponibilidadeSalvo =
    dadosDisponibilidade.escopoDisponibilizacao ??
    (dadosDisponibilidade.motivo
      ? "NAO_DESEJO_RECEBER_OPORTUNIDADES"
      : fluxo.contextoFluxo.escopoDisponibilizacao);
  const opcoes = fluxo.detalhesEtapa?.opcoes?.length
    ? fluxo.detalhesEtapa.opcoes
    : [
        "TODOS_OS_PERIODOS",
        "APENAS_ALGUNS_PERIODOS",
        "NAO_DESEJO_RECEBER_OPORTUNIDADES",
      ];

  useEffect(() => {
    form.setFieldsValue({
      escopoDisponibilizacao:
        escopoDisponibilidadeSalvo,
      motivo: dadosDisponibilidade.motivo,
      observacao: dadosDisponibilidade.observacao,
    });
  }, [
    escopoDisponibilidadeSalvo,
    dadosDisponibilidade.motivo,
    dadosDisponibilidade.observacao,
    fluxo.contextoFluxo.escopoDisponibilizacao,
    form,
  ]);

  return (
    <OnboardingFrame
      fluxo={fluxo}
      title="Gostaria de receber oportunidades de aluguel ou troca para seus períodos?"
      description={
        <>
          <strong>Seus períodos não ficam bloqueados.</strong> Quando surgir uma
          oportunidade, entraremos em contato e você decide se deseja aceitá-la.
        </>
      }
    >
      <Form
        layout="vertical"
        form={form}
        initialValues={{
          escopoDisponibilizacao:
            escopoDisponibilidadeSalvo,
          motivo: dadosDisponibilidade.motivo,
          observacao: dadosDisponibilidade.observacao,
        }}
        onFinish={(values) => {
          if (values.escopoDisponibilizacao === "NAO_DESEJO_RECEBER_OPORTUNIDADES") {
            onConcluir({
              escopoDisponibilizacao: values.escopoDisponibilizacao,
              motivo: values.motivo,
              observacao: values.motivo === "OUTRO" ? values.observacao : undefined,
            });
            return;
          }

          onConcluir({
            escopoDisponibilizacao: values.escopoDisponibilizacao,
          });
        }}
      >
        <Form.Item
          name="escopoDisponibilizacao"
          rules={[{ required: true, message: "Selecione uma opção." }]}
        >
          <Radio.Group style={{ width: "100%" }}>
            <Space direction="vertical" size={12} style={{ width: "100%" }}>
              {opcoes.map((opcao) => {
                const opcaoDisponibilidade = scopeLabels[opcao];
                const selecionada = escopo === opcao;

                return (
                  <Radio
                    key={opcao}
                    value={opcao}
                    style={{
                      width: "100%",
                      margin: 0,
                      padding: "16px 18px",
                      border: `1px solid ${selecionada ? "#003644" : "#d9e4e8"}`,
                      borderRadius: 16,
                      background: selecionada ? "#eef8f7" : "#fff",
                      transition: "border-color 160ms ease, background 160ms ease",
                    }}
                  >
                    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      <Typography.Text strong>
                        {opcaoDisponibilidade?.titulo ?? opcao}
                      </Typography.Text>
                      {opcaoDisponibilidade ? (
                        <Typography.Text type="secondary">
                          {opcaoDisponibilidade.descricao}
                        </Typography.Text>
                      ) : null}
                    </div>
                  </Radio>
                );
              })}
            </Space>
          </Radio.Group>
        </Form.Item>

        {escopo === "NAO_DESEJO_RECEBER_OPORTUNIDADES" ? (
          <>
            <Form.Item
              label="Motivo"
              name="motivo"
              rules={[{ required: true, message: "Selecione o motivo." }]}
            >
              <Select
                options={MOTIVO_OPTIONS}
                placeholder="Selecione o motivo"
                onChange={(value) => {
                  if (value !== "OUTRO") {
                    form.setFieldValue("observacao", undefined);
                  }
                }}
              />
            </Form.Item>

            {motivo === "OUTRO" ? (
              <Form.Item
                label="Descreva o motivo"
                name="observacao"
                rules={[
                  {
                    required: true,
                    message: "Informe uma observação para o motivo OUTRO.",
                  },
                ]}
              >
                <Input.TextArea rows={4} />
              </Form.Item>
            ) : null}
          </>
        ) : null}

        <SubmitButton label="Continuar" loading={submitting} />
      </Form>
    </OnboardingFrame>
  );
}
